import React, { useEffect, useState } from "react";
import { EngineModelSelect } from "@/components/EngineModelSelect";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { noFollowUps, testScriptCsv } from "@/lib/testScript";

// Admin test script for follow-ups (2026-10-27): one answer to read aloud for each
// question of one interview, with what the interviewer should do next. Outcomes and
// English levels are mixed across the interview; "Write again" makes a new mix.
// Nothing is stored. "Run it" is a dry run: each answer goes through the live
// evidence check as its transcript (no speech-to-text, audio or screens).
const ENGLISH_LABELS = { strong: "Strong English", intermediate: "Intermediate English", weak: "Weak English" };
export default function AdminTestScript() {
  const { sessionId } = useParams();
  const [script, setScript] = useState(null);
  const [error, setError] = useState("");
  const [run, setRun] = useState(0);
  const [results, setResults] = useState(null); // dry-run outcome, per question number
  const [running, setRunning] = useState(false);
  // Simulated interview (SCORING_PLAN.md §3.1): a hidden copy answered with this
  // script, scored and completed in the background; polled until done.
  const [simulation, setSimulation] = useState(null);
  // One English level for the whole simulated interview (SCORING_PLAN.md §5a).
  const [simEnglish, setSimEnglish] = useState("intermediate");
  const [engine, setEngine] = useState(""); // engine model for the dry run and the simulation
  // One English level for every answer of the printed script; "" mixes the levels.
  const [scriptEnglish, setScriptEnglish] = useState("");

  useEffect(() => {
    let alive = true;
    setScript(null);
    setResults(null);
    setError("");
    api.post(`/admin/test-script/${encodeURIComponent(sessionId)}`, null,
      { timeout: 120000, params: scriptEnglish ? { english: scriptEnglish } : {} })
      .then(({ data }) => { if (alive) setScript(data); })
      .catch((e) => {
        if (!alive) return;
        const message = formatApiErrorDetail(e);
        setError(message);
        toast.error(message);
      });
    return () => { alive = false; };
  }, [sessionId, run, scriptEnglish]);

  const runIt = async () => {
    setRunning(true);
    try {
      const questions = script.questions
        .filter((q) => q.say)
        .map((q) => ({ number: q.number, case: q.case, say: q.say, flagged: Boolean(q.note) }));
      const { data } = await api.post(`/admin/test-script/${encodeURIComponent(sessionId)}/run`, { questions, engine_model: engine }, { timeout: 120000 });
      setResults({ ...data, byNumber: Object.fromEntries(data.results.map((r) => [r.number, r])) });
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!simulation?.simulated_session_id || simulation.status !== "running") return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/simulations/${simulation.simulated_session_id}`);
        setSimulation(data);
        if (data.status === "failed") toast.error(`The simulation failed (${data.error || "unknown error"}).`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e));
        setSimulation((s) => ({ ...s, status: "failed" }));
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [simulation]);

  const simulate = async () => {
    if (!window.confirm(`Make a simulated copy of this interview, answer it with this script in ${ENGLISH_LABELS[simEnglish] || simEnglish} throughout, score it and build its report? It takes about 1–2 minutes. The copy is hidden from history and metrics.`)) return;
    try {
      const questions = script.questions
        .filter((q) => q.say)
        .map((q) => ({ number: q.number, case: q.case, say: q.say, english: q.english || "" }));
      const { data } = await api.post(`/admin/test-script/${encodeURIComponent(sessionId)}/simulate`, { questions, english: simEnglish, engine_model: engine });
      setSimulation({ ...data, status: "running", done: 0, total: questions.length });
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const downloadCsv = () => {
    const blob = new Blob([testScriptCsv(script, results)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `test-script_${sessionId}_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)] print:bg-white" data-testid="admin-test-script">
      <div className="print:hidden"><AdminNav title="Follow-up test script" /></div>
      <main className="max-w-4xl mx-auto p-6 space-y-6 print:p-0">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">Test script{script?.role_title ? `: ${script.role_title}` : ""}</h1>
            <div className="text-xs text-[color:var(--text-secondary)] font-mono break-all">{sessionId}</div>
            <p className="mt-2 text-xs text-[color:var(--text-secondary)] max-w-2xl">
              Read the answer for each question, then check that the interviewer does what it says. The expected
              outcomes and English levels are mixed across the interview; “Write again” makes a new mix. Answers are written
              from the question and its hidden points only, never the résumé or job description.
            </p>
          </div>
          <div className="flex gap-2 print:hidden">
            <EngineModelSelect value={engine} onChange={setEngine} className="rounded-full px-3 py-2 text-sm" testId="test-script-engine" />
            <button type="button" onClick={runIt} disabled={!script || running} data-testid="test-script-run"
              title="Dry run: each answer goes through the evidence check as its transcript. Nothing is saved."
              className="rounded-full border border-[color:var(--brand)] px-4 py-2 text-sm text-[color:var(--brand)] disabled:opacity-50">
              {running ? "Running…" : results ? "Run again" : "Run it"}
            </button>
            <select value={simEnglish} onChange={(e) => setSimEnglish(e.target.value)} aria-label="English level for the simulation"
              title="The simulated candidate speaks at this level on every question"
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-2 text-sm" data-testid="test-script-sim-english">
              {Object.entries(ENGLISH_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <button type="button" onClick={simulate} disabled={!script || simulation?.status === "running"} data-testid="test-script-simulate"
              title="A hidden copy answered with this script, scored, with a real report"
              className="rounded-full border border-[color:var(--brand)] px-4 py-2 text-sm text-[color:var(--brand)] disabled:opacity-50">
              {simulation?.status === "running" ? "Simulating…" : "Simulate interview"}
            </button>
            <span className="self-center text-xs text-[color:var(--text-secondary)]" data-testid="test-script-sim-note">
              Simulation: every answer in {ENGLISH_LABELS[simEnglish] || simEnglish}
            </span>
            <select value={scriptEnglish} onChange={(e) => setScriptEnglish(e.target.value)}
              aria-label="English level of the script" disabled={running}
              title="English level of every answer in the printed script; changing it writes a new script"
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-2 text-sm" data-testid="test-script-english">
              <option value="">Script English: mixed</option>
              <option value="strong">Script English: all strong</option>
              <option value="intermediate">Script English: all intermediate</option>
              <option value="weak">Script English: all weak</option>
            </select>
            <button type="button" onClick={() => setRun((n) => n + 1)} disabled={(!script && !error) || running}
              className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-sm disabled:opacity-50">
              Write again
            </button>
            <button type="button" onClick={downloadCsv} disabled={!script} data-testid="test-script-csv"
              title="The script and, after Run it, the results"
              className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-sm disabled:opacity-50">
              Download CSV
            </button>
            <button type="button" onClick={() => window.print()} disabled={!script} data-testid="test-script-print"
              className="rounded-full bg-[color:var(--brand)] px-4 py-2 text-sm text-white disabled:opacity-50">
              Print
            </button>
          </div>
        </header>

        {script && !script.follow_ups_enabled && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm print:hidden" role="status">
            Follow-up questions are <strong>off</strong> (Admin → Voice Check), so every answer will just move on.
          </div>
        )}
        {noFollowUps(script) && (
          <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm" role="alert" data-testid="test-script-no-follow-ups">
            <strong>This interview has no follow-ups on any question,</strong> so every answer will move on whatever it says
            and the script can’t test follow-ups. It was probably prepared before follow-ups were added. Prepare a new
            interview and open its test script instead.
          </div>
        )}
        {!script && !error && (
          <div className="text-sm text-[color:var(--text-secondary)]" role="status">Writing the test script… this takes about 10–30 seconds.</div>
        )}
        {error && <div className="text-sm text-rose-700">{error}</div>}

        {results && (
          <div className={`rounded-xl border p-3 text-sm ${results.passed + results.inconclusive === results.total ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}
            data-testid="test-script-run-summary" role="status">
            <span className="font-semibold">
              Dry run: {results.passed} of {results.total - results.inconclusive} as expected
              {results.inconclusive ? `, ${results.inconclusive} inconclusive` : ""}.
            </span>
            <span className="text-xs text-[color:var(--text-secondary)]">
              {" "}Check time: typical {(results.typical_ms / 1000).toFixed(1)} s, slowest {(results.slowest_ms / 1000).toFixed(1)} s.
              {" "}No speech-to-text, audio or screens; nothing saved to the interview.
            </span>
          </div>
        )}

        {simulation && (
          <section className="rounded-xl border border-[color:var(--border-stone)] bg-white p-4 text-sm space-y-3 print:hidden" data-testid="test-script-simulation">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold">
                Simulated interview{" "}
                <span className="font-normal text-[color:var(--text-secondary)]">
                  {simulation.status === "running" && `· answering and scoring… ${simulation.done || 0} of ${simulation.total || 0}`}
                  {simulation.status === "done" && "· done"}
                  {simulation.status === "failed" && `· failed (${simulation.error || "error"})`}
                </span>
              </div>
              {simulation.status === "done" && (
                <a href={`/report/${simulation.simulated_session_id}`} target="_blank" rel="noopener noreferrer"
                  className="rounded-full bg-[color:var(--brand)] px-4 py-2 text-sm text-white" data-testid="test-script-simulation-report">
                  Open report
                </a>
              )}
            </div>
            {simulation.status === "done" && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[color:var(--text-secondary)]">
                    <tr><th className="py-1 pr-3">Q</th><th className="pr-3">Case</th><th className="pr-3">English</th>
                      <th className="pr-3">What happened</th><th className="pr-3">Interview Performance</th><th className="pr-3">English score</th><th>Overall</th></tr>
                  </thead>
                  <tbody>
                    {(simulation.results || []).map((r) => (
                      <tr key={r.number} className="border-t border-[color:var(--border-stone)] align-top">
                        <td className="py-1 pr-3">{r.number}</td>
                        <td className="pr-3">{r.case}</td>
                        <td className="pr-3">{r.english || "—"}</td>
                        <td className="pr-3">
                          {r.repeated ? "Repeat, then " : ""}
                          {r.action === "follow_up" ? `Follow-up: “${r.follow_up}”` : "Moved on"}
                        </td>
                        <td className="pr-3">{r.interview_performance_score ?? "—"}</td>
                        <td className="pr-3">{r.english_score ?? "—"}</td>
                        <td>{r.overall ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {script?.mix && (
          <div className="rounded-xl border border-[color:var(--border-stone)] bg-white p-3 text-xs" data-testid="test-script-mix">
            <span className="font-semibold">This mix: </span>
            {Object.entries(script.mix.outcomes).map(([label, n]) => `${label} ×${n}`).join(" · ")}
            <span className="text-[color:var(--text-secondary)]">
              {" "}· Run it only: {Object.entries(script.mix.english).map(([level, n]) => `${ENGLISH_LABELS[level] || level} ×${n}`).join(" · ")}
            </span>
          </div>
        )}

        {script?.questions.map((q) => (
          <section key={q.number} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 break-inside-avoid"
            data-testid={`test-script-q${q.number}`}>
            <div className="text-xs uppercase tracking-widest text-[color:var(--brand)]">
              Question {q.number}{q.has_follow_ups ? "" : " · no follow-ups for this question"}
            </div>
            <h2 className="mt-1 font-semibold leading-snug">{q.text}</h2>
            <div className="mt-4 grid gap-1">
              <div className="text-xs font-semibold">
                {q.label}
                {q.english && <span className="ml-2 font-normal text-[color:var(--text-secondary)]">· {ENGLISH_LABELS[q.english] || q.english}</span>}
              </div>
              <div className="rounded-lg bg-[color:var(--bg-surface-hover)] p-3 text-sm leading-relaxed">
                {q.say ? `“${q.say}”` : <span className="italic text-[color:var(--text-secondary)]">No answer was written for this question. Try “Write again”.</span>}
              </div>
              <div className="text-xs"><span className="font-semibold">Expected:</span> {q.expect}</div>
              {q.note && <div className="text-xs text-amber-700">{q.note}</div>}
              {results?.byNumber[q.number] && (() => {
                const r = results.byNumber[q.number];
                const inconclusive = r.status === "inconclusive";
                const tone = r.ok ? "bg-emerald-50 text-emerald-900" : inconclusive ? "bg-amber-50 text-amber-900" : "bg-rose-50 text-rose-900";
                return (
                  <div className={`mt-1 rounded-lg px-3 py-2 text-xs ${tone}`} data-testid={`test-script-result-q${q.number}`}>
                    <span className="font-semibold">{r.ok ? "✓ Actual:" : inconclusive ? "⚠ Inconclusive. Actual:" : "✗ Actual:"}</span> {r.actual}
                    {!r.ok && r.error_type && <span> ({r.error_type})</span>}
                    {inconclusive && <div className="mt-0.5">The answer may still touch the point, so this miss may not be the check’s fault.</div>}
                    <div className="mt-0.5 opacity-80">Why: {r.reason || "—"} · {(r.ms / 1000).toFixed(1)} s</div>
                  </div>
                );
              })()}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
